<template>
  <div class="stable-popover-root">
    <div
      ref="triggerRef"
      class="stable-popover-trigger"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <slot name="trigger" />
    </div>

    <div
      ref="popoverRef"
      class="stable-popover-content"
      :style="contentStyle"
      popover
      @mouseenter="onPopoverMouseEnter"
      @mouseleave="onPopoverMouseLeave"
    >
      <slot name="content" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  placement: {
    type: String,
    default: 'bottom',
    validator: (value) =>
      [
        'top',
        'bottom',
        'left',
        'right',
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
      ].includes(value),
  },
  mobilePlacement: {
    type: String,
    default: null,
    validator: (value) =>
      !value ||
      [
        'top',
        'bottom',
        'left',
        'right',
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
      ].includes(value),
  },
  offset: {
    type: Number,
    default: 8,
  },
  popoverType: {
    type: String,
    default: 'tooltip',
  },
  trigger: {
    type: String,
    default: 'hover',
  },
  background: {
    type: String,
    default: '#262626',
  },
})

const triggerRef = ref(null)
const popoverRef = ref(null)
const triggerCoordinates = ref({ top: 0, bottom: 0, left: 0, right: 0 })
const viewportHeight = ref(0)
const viewportWidth = ref(0)
const isMobile = ref(false)
let hideTimeout = null

function checkIsMobile() {
  isMobile.value = window.innerWidth < 768
}

const contentStyle = computed(() => {
  const triggerWidth =
    triggerCoordinates.value.right - triggerCoordinates.value.left
  const triggerHeight =
    triggerCoordinates.value.bottom - triggerCoordinates.value.top

  const activePlacement =
    isMobile.value && props.mobilePlacement
      ? props.mobilePlacement
      : props.placement

  let position = {}

  switch (activePlacement) {
    case 'bottom':
      position = {
        top: `${triggerCoordinates.value.bottom + props.offset}px`,
        left: `${triggerCoordinates.value.left + triggerWidth / 2}px`,
        transform: 'translateX(-50%)',
      }
      break
    case 'top':
      position = {
        top: 'unset',
        bottom: `${viewportHeight.value - triggerCoordinates.value.top + props.offset}px`,
        left: `${triggerCoordinates.value.left + triggerWidth / 2}px`,
        transform: 'translateX(-50%)',
      }
      break
    case 'left':
      position = {
        top: `${triggerCoordinates.value.top + triggerHeight / 2}px`,
        right: `${viewportWidth.value - triggerCoordinates.value.left + props.offset}px`,
        transform: 'translateY(-50%)',
      }
      break
    case 'right':
      position = {
        top: `${triggerCoordinates.value.top + triggerHeight / 2}px`,
        left: `${triggerCoordinates.value.right + props.offset}px`,
        transform: 'translateY(-50%)',
      }
      break
    case 'top-left':
      position = {
        top: 'unset',
        bottom: `${viewportHeight.value - triggerCoordinates.value.top + props.offset}px`,
        right: `${viewportWidth.value - triggerCoordinates.value.left - 40}px`,
      }
      break
    case 'top-right':
      position = {
        top: 'unset',
        bottom: `${viewportHeight.value - triggerCoordinates.value.top + props.offset}px`,
        left: `${triggerCoordinates.value.right}px`,
      }
      break
    case 'bottom-left':
      position = {
        top: `${triggerCoordinates.value.bottom + props.offset}px`,
        right: `${viewportWidth.value - triggerCoordinates.value.left}px`,
      }
      break
    case 'bottom-right':
      position = {
        top: `${triggerCoordinates.value.bottom + props.offset}px`,
        left: `${triggerCoordinates.value.right}px`,
      }
      break
  }

  return {
    position: 'fixed',
    inset: 'unset',
    margin: 0,
    ...position,
  }
})

function updateTriggerCoordinates() {
  if (!triggerRef.value) return

  const rect = triggerRef.value.getBoundingClientRect()
  triggerCoordinates.value = {
    top: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    right: rect.right,
  }
}

function updateViewportSize() {
  viewportHeight.value = document.documentElement.clientHeight
  viewportWidth.value = document.documentElement.clientWidth
}

function showPopover() {
  if (!popoverRef.value) return

  updateViewportSize()
  updateTriggerCoordinates()
  popoverRef.value.showPopover()
}

function hidePopover() {
  if (!popoverRef.value) return
  popoverRef.value.hidePopover()
}

function hidePopoverIfNotHovered() {
  const isContentHovered = popoverRef.value?.matches(':hover')
  const isTriggerHovered = triggerRef.value?.matches(':hover')

  if (isContentHovered || isTriggerHovered) return
  hidePopover()
}

function onMouseEnter() {
  if (props.trigger !== 'hover') return

  clearTimeout(hideTimeout)
  showPopover()
}

function onMouseLeave() {
  if (props.trigger !== 'hover') return

  hideTimeout = setTimeout(() => {
    hidePopoverIfNotHovered()
  }, 150)
}

function onPopoverMouseEnter() {
  clearTimeout(hideTimeout)
}

function onPopoverMouseLeave() {
  hideTimeout = setTimeout(() => {
    hidePopoverIfNotHovered()
  }, 150)
}

function handleWindowResize() {
  updateViewportSize()
  updateTriggerCoordinates()
  checkIsMobile()
}

function addEventListeners() {
  window.addEventListener('resize', handleWindowResize)
  document.addEventListener('scroll', updateTriggerCoordinates, {
    capture: true,
  })
}

function removeEventListeners() {
  window.removeEventListener('resize', handleWindowResize)
  document.removeEventListener('scroll', updateTriggerCoordinates, {
    capture: true,
  })
}

function toggleEventListeners(event) {
  if (event.newState === 'open') {
    addEventListeners()
  } else {
    removeEventListeners()
  }
}

onMounted(() => {
  if (!popoverRef.value) return
  popoverRef.value.addEventListener('beforetoggle', toggleEventListeners)
  checkIsMobile()
})

onBeforeUnmount(() => {
  if (popoverRef.value) {
    popoverRef.value.removeEventListener('beforetoggle', toggleEventListeners)
  }
  removeEventListeners()
  clearTimeout(hideTimeout)
})
</script>

<style scoped>
.stable-popover-root {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.stable-popover-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.stable-popover-content {
  position: v-bind('contentStyle.position');
  inset: v-bind('contentStyle.inset');
  top: v-bind('contentStyle.top');
  bottom: v-bind('contentStyle.bottom');
  left: v-bind('contentStyle.left');
  right: v-bind('contentStyle.right');
  transform: v-bind('contentStyle.transform');
  margin: v-bind('contentStyle.margin');
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  padding: 12px;
  background: v-bind('props.background');
  border: 1px solid rgba(208, 208, 208, 0.07);
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  max-width: 35vh;
  max-height: 295px;
  overflow-y: auto;
  transition:
    opacity 0.12s,
    overlay 0.12s allow-discrete,
    display 0.12s allow-discrete;
}

.stable-popover-content:popover-open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

@starting-style {
  .stable-popover-content:popover-open {
    opacity: 0;
  }
}
</style>
